import { ChevronLeft, ChevronRight } from "lucide-react";

export interface PaginationProps {
  /** 1-indexed current page. */
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
  /** When true, all nav buttons are disabled (used to avoid double-click while fetching). */
  disabled?: boolean;
  className?: string;
}

const NAV_BTN =
  "inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card text-foreground shadow-sm hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-card";

export function Pagination({
  page,
  totalPages,
  onChange,
  disabled,
  className,
}: PaginationProps) {
  if (totalPages <= 1) return null;

  const safePage = Math.max(1, Math.min(page, totalPages));
  const prevDisabled = disabled || safePage <= 1;
  const nextDisabled = disabled || safePage >= totalPages;

  const wrapperClasses = [
    "flex items-center justify-center gap-3",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <nav aria-label="Pagination" className={wrapperClasses}>
      <button
        type="button"
        onClick={() => onChange(safePage - 1)}
        disabled={prevDisabled}
        aria-label="Previous page"
        className={NAV_BTN}
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </button>
      <span
        className="text-sm text-muted-foreground"
        aria-live="polite"
        aria-atomic="true"
      >
        {safePage} / {totalPages}
      </span>
      <button
        type="button"
        onClick={() => onChange(safePage + 1)}
        disabled={nextDisabled}
        aria-label="Next page"
        className={NAV_BTN}
      >
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </button>
    </nav>
  );
}
